import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SignInButton } from "@clerk/clerk-react";
import { SiteNav } from "./SiteNav";
import { SiteFooter } from "./SiteFooter";
import { usePageTitle } from "./usePageTitle";
import { PetalsCoin } from "./PetalsCoin";
import { PlayButton } from "./PlayButton";
import { authEnabled } from "../AuthGate";
import { useWho } from "./visitor";
import { useMembership, clubDate } from "./useMembership";
import { SUB_COPY, SUBSCRIBER_PETAL_MULTIPLIER } from "../../sim/subscription";
import { EXCHANGE_DAILY_PETALS } from "../../sim/exchange";
import {
  startSubscriptionCheckout, openBillingPortal, cancelSubscription, resumeSubscription, announceMembership,
  type Membership,
} from "../../sim/membership";
import { ClubCrest, PetalDrift, useClubPrizes } from "./clubArt";
import { track } from "../../sim/analytics";

export { ClubCrest };

/**
 * GARDEN CLUB ON THE WEBSITE — the /garden-club landing page, and every smaller surface that points at it:
 * the home page's band and top button, the nav pill, and the Profile page's membership panel. They live in
 * one file so the club reads as one voice.
 *
 * EVERY SURFACE HAS TWO VERSIONS, chosen by `useMembership`, and they do not overlap: a member is never shown
 * a price, a pitch or a join button — they see their membership (what it is doing for them, when it renews,
 * how to manage it). Everyone else sees the invitation. While the answer is loading the club's slot stays
 * quiet rather than guessing, so a member never sees the pitch flash past.
 *
 * TONE is sim/subscription.ts's rule, not a preference here: a non-member LOSES NOTHING. Their prizes are
 * WAITING, never missed, and the comparison table says what free play keeps before what the club adds.
 * Strings that are the club's own voice come from `SUB_COPY`; page-length copy lives here.
 *
 * THE LOOK is the game's own (STYLE_GUIDE.md): a deep cold-green night lit from within — spectral teal glow
 * as the main light, lantern gold and petal pink as the warm counterpoint, off-white type, never white.
 */

// ---------------------------------------------------------------------------
//  the join button — the only place checkout starts
// ---------------------------------------------------------------------------

/** JOIN. Signed out → sign in first; no payments on this server → says so; otherwise straight to Stripe. */
function JoinButton({ m, big = false, label }: { m: Membership; big?: boolean; label?: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const cls = `gc-join${big ? " big" : ""}`;
  const text = label ?? (m.status === "lapsed" ? "Rejoin Garden Club" : SUB_COPY.cta);

  if (!m.paymentsEnabled) {
    return (
      <div className="gc-join-wrap">
        <button type="button" className={cls} disabled>Opening soon</button>
        <span className="gc-join-note">Memberships aren't open yet. Check back soon.</span>
      </div>
    );
  }
  if (m.joinPaused) {
    return (
      <div className="gc-join-wrap">
        <button type="button" className={cls} disabled data-testid="club-join-paused">Opens at full release</button>
        <span className="gc-join-note">Garden Club opens with the full game. Joining is paused during the Kickstarter demo.</span>
      </div>
    );
  }
  if (authEnabled && !m.signedIn) {
    return (
      <div className="gc-join-wrap">
        <SignInButton mode="modal"><button type="button" className={cls} onClick={() => track("cta_click", { id: "club.join" })}>Sign in to join</button></SignInButton>
        <span className="gc-join-note">A free Shelf Garden account is all you need. Your membership attaches to it.</span>
      </div>
    );
  }
  async function go() {
    track("cta_click", { id: "club.join" });   // the Garden Club funnel's "join clicked" (admin → Analytics → Economy)
    setBusy(true); setErr("");
    try { window.location.href = await startSubscriptionCheckout(); }
    catch (e) { setErr(String((e as Error).message || e)); setBusy(false); }
  }
  return (
    <div className="gc-join-wrap">
      <button type="button" className={cls} disabled={busy} onClick={() => void go()}>
        {busy ? "Opening secure checkout…" : text}
      </button>
      <span className="gc-join-note">{SUB_COPY.price} · cancel any time · secure checkout by Stripe</span>
      {err && <span className="gc-err" role="alert">{err}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  the page
// ---------------------------------------------------------------------------

export function GardenClub() {
  usePageTitle("Garden Club · Shelf Garden");
  const { m, member, loading, refresh } = useMembership();
  const joined = new URLSearchParams(useLocation().search).has("joined");
  const confirming = useConfirmJoin(joined && !member && !loading, refresh);
  // back from checkout and the membership is in (at once, or after polling): tell any open game tab
  useEffect(() => { if (joined && member) announceMembership(m.subscription); }, [joined, member, m.subscription]);

  return (
    <div className="site gc">
      <SiteNav />
      {loading ? <div className="gc-hero gc-hero-loading" aria-busy="true"><ClubCrest size={120} /></div>
        : member ? <MemberHero m={m} />
        : joined && confirming !== "gave-up" ? <ConfirmingHero />
        : <InviteHero m={m} slow={confirming === "gave-up"} />}
      {!loading && (
        <div className="gc-wrap">
          <Perks member={member} />
          <Prizes member={member} waiting={m.waiting} />
          {!member && <HowItWorks />}
          {!member && <Compare />}
          <ClubFaq member={member} />
          {!member && !joined && (
            <section className="gc-final">
              <ClubCrest size={72} glow />
              <h2>Garden Club</h2>
              <p>{SUB_COPY.perks.join(" · ")}</p>
              <JoinButton m={m} big />
            </section>
          )}
        </div>
      )}
      <SiteFooter />
    </div>
  );
}

/**
 * BACK FROM STRIPE (`?joined=1`). The redirect only means the browser came back — the membership arrives by
 * webhook, usually within a second or two — so poll the server for up to ~40 s. When it lands the page turns
 * into the member's, and `GardenClub` tells any open game tab (`announceMembership`), which then re-reads the
 * server and switches the perks on.
 */
function useConfirmJoin(active: boolean, refresh: () => Promise<Membership>): "waiting" | "gave-up" | null {
  const [state, setState] = useState<"waiting" | "gave-up" | null>(active ? "waiting" : null);
  const tries = useRef(0);
  useEffect(() => {
    if (!active) return;
    setState("waiting");
    let alive = true;
    const t = window.setInterval(async () => {
      tries.current += 1;
      const m = await refresh();
      if (!alive) return;
      if (m.status === "active") window.clearInterval(t);
      else if (tries.current >= 20) { setState("gave-up"); window.clearInterval(t); }
    }, 2000);
    return () => { alive = false; window.clearInterval(t); };
  }, [active, refresh]);
  return state;
}

function InviteHero({ m, slow }: { m: Membership; slow: boolean }) {
  const lapsed = m.status === "lapsed";
  return (
    <header className="gc-hero">
      <div className="gc-hero-bg" aria-hidden="true" />
      <PetalDrift />
      <div className="gc-hero-inner">
        <ClubCrest size={128} glow />
        <span className="gc-kicker">{lapsed ? "Welcome back" : "Membership"}</span>
        <h1 className="gc-title">The Garden Club</h1>
        <p className="gc-lede">
          {lapsed
            ? SUB_COPY.lapsed
            : <>Earn {SUBSCRIBER_PETAL_MULTIPLIER}× Petals, collect all your achievement prizes, and trade as many coins for Petals as you like at the exchange, for {SUB_COPY.price.replace("/month", " a month")}.</>}
        </p>
        {m.waiting > 0 && (
          <p className="gc-waiting">🎁 <b>{SUB_COPY.heldCount(m.waiting)}.</b> {SUB_COPY.lockedHint}</p>
        )}
        {slow ? (
          // NO JOIN BUTTON HERE: this player has just paid and the webhook is late — a second checkout would be
          // a second subscription. (The server refuses one too, but only once it can see the first.)
          <>
            <p className="gc-note" role="status">
              Thank you! Stripe is still confirming your payment, and your membership will switch on within a few minutes.
              Refresh this page in a moment, or start playing; the game picks it up on its own.
            </p>
            <button type="button" className="gc-ghost" onClick={() => window.location.reload()}>Refresh</button>
          </>
        ) : <JoinButton m={m} big />}
        <a className="gc-more" href="#gc-perks">See what you get{" "}
          <svg className="gc-more-chev" width="12" height="8" viewBox="0 0 12 8" aria-hidden="true"><path d="M1.5 1.75l4.5 4.5 4.5-4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </a>
      </div>
    </header>
  );
}

function ConfirmingHero() {
  return (
    <header className="gc-hero">
      <div className="gc-hero-bg" aria-hidden="true" />
      <PetalDrift />
      <div className="gc-hero-inner" role="status">
        <ClubCrest size={128} glow />
        <span className="gc-kicker">Almost there</span>
        <h1 className="gc-title">Welcome to the Garden Club!</h1>
        <p className="gc-lede"><span className="gc-spin" aria-hidden="true" /> Confirming your membership with Stripe…</p>
      </div>
    </header>
  );
}

function MemberHero({ m }: { m: Membership }) {
  const who = useWho();
  const s = m.subscription;
  return (
    <header className="gc-hero member">
      <div className="gc-hero-bg" aria-hidden="true" />
      <PetalDrift n={18} />
      <div className="gc-hero-inner">
        <ClubCrest size={128} glow />
        <span className="gc-kicker member">✦ Member ✦</span>
        <h1 className="gc-title">Welcome to the Garden Club{who.name ? `, ${who.name}` : ""}</h1>
        <p className="gc-lede">{SUB_COPY.memberLine}</p>
        <div className="gc-status">
          {s.cancelAtPeriodEnd && s.periodEnd ? SUB_COPY.endingLine(clubDate(s.periodEnd))
            : s.periodEnd ? <>Your membership renews on <b>{clubDate(s.periodEnd)}</b>.</>
            : <>Your membership is active.</>}
        </div>
        {m.waiting > 0 && <p className="gc-waiting">🎁 <b>{SUB_COPY.heldCount(m.waiting)}.</b> Collect them from the Achievements panel in the game.</p>}
        <div className="gc-hero-actions">
          <PlayButton label={`▶ ${m.waiting > 0 ? "Collect your prizes" : "Play"}`} className="gc-join big" trackId="club.play" />
          <Link to="/profile#garden-club" className="gc-ghost">Manage membership</Link>
        </div>
      </div>
    </header>
  );
}

function Perks({ member }: { member: boolean }) {
  return (
    <section className="gc-section" id="gc-perks" aria-labelledby="gc-perks-h">
      <h2 id="gc-perks-h" className="gc-h2">{member ? "Your member perks" : "What membership includes"}</h2>
      <div className="gc-perks">
        <article className="gc-perk petals">
          {member && <span className="gc-on">Active</span>}
          <div className="gc-perk-art">
            <PetalsCoin size={84} />
            <span className="gc-perk-x">{SUBSCRIBER_PETAL_MULTIPLIER}×</span>
          </div>
          <h3>{SUBSCRIBER_PETAL_MULTIPLIER}× Petals</h3>
          <p>Petals from your daily check-ins and achievement rewards are multiplied by {SUBSCRIBER_PETAL_MULTIPLIER}, so you can buy more of the Shop's skins and seeds sooner.</p>
        </article>
        <article className="gc-perk prizes">
          {member && <span className="gc-on">Active</span>}
          <div className="gc-perk-art gc-perk-gift" aria-hidden="true">🎁</div>
          <h3>Achievement prizes</h3>
          <p>Hundreds of achievements come with a Garden Club prize: decor, seeds, pot, rack and floor skins, and a trophy for every plant you master. Members can collect all of them, including prizes for achievements they've already earned.</p>
        </article>
        <article className="gc-perk exchange">
          {member && <span className="gc-on">Active</span>}
          <div className="gc-perk-art gc-perk-inf" aria-hidden="true">⇄</div>
          <h3>Unlimited Petal exchange</h3>
          <p>Turn your coins into Petals with no daily limit. The free exchange stops at {EXCHANGE_DAILY_PETALS} a day.</p>
        </article>
      </div>
    </section>
  );
}

function Prizes({ member, waiting }: { member: boolean; waiting: number }) {
  const { count, prizes } = useClubPrizes(12);
  if (prizes.length === 0) return null;
  return (
    <section className="gc-section" aria-labelledby="gc-prizes-h">
      <h2 id="gc-prizes-h" className="gc-h2">{member ? "Your prizes" : "A few of the prizes"}</h2>
      <p className="gc-sub">
        {count} achievements come with a Garden Club prize.{" "}
        {member
          ? waiting > 0 ? `You have ${waiting} ready to collect in the game.` : "New ones appear as you earn achievements."
          : "They stay there until you collect them."}
      </p>
      <div className="gc-prize-grid">
        {prizes.map(({ src, name }) => (
          <figure key={src} className="gc-prize">
            <img src={src} alt={name} loading="lazy" onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = "none"; }} />
            <figcaption aria-hidden="true">{name}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { t: "Sign up", s: `${SUB_COPY.price}, paid securely through Stripe. Your membership attaches to your Shelf Garden account.` },
    { t: "Your perks switch on", s: `${SUBSCRIBER_PETAL_MULTIPLIER}× Petals and unlimited exchange start right away, in the game, on every device you play on.` },
    { t: "Collect your prizes", s: "Open Achievements in the game. The prizes you've earned are there, ready to collect." },
  ];
  return (
    <section className="gc-section" aria-labelledby="gc-how-h">
      <h2 id="gc-how-h" className="gc-h2">How it works</h2>
      <ol className="gc-steps">
        {steps.map((s, i) => (
          <li key={s.t}><span className="gc-step-n" aria-hidden="true">{i + 1}</span><div><b>{s.t}</b><p>{s.s}</p></div></li>
        ))}
      </ol>
    </section>
  );
}

/** FREE vs CLUB — what free play keeps comes first, and nothing in the free column is a loss. */
function Compare() {
  const rows: [string, string, string][] = [
    ["The whole game: every fish, insect and scene, and every plant you grow into", "✓", "✓"],
    ["Achievements, badges and points", "✓", "✓"],
    ["Petals from check-ins and achievements", "1×", `${SUBSCRIBER_PETAL_MULTIPLIER}×`],
    ["Achievement prizes", "Held for you", "Collect any time"],
    ["Petal exchange", `${EXCHANGE_DAILY_PETALS} a day`, "Unlimited"],
  ];
  return (
    <section className="gc-section" aria-labelledby="gc-cmp-h">
      <h2 id="gc-cmp-h" className="gc-h2">Free or Garden Club</h2>
      <div className="gc-compare" role="table" aria-label="Free play compared with Garden Club">
        <div className="gc-cmp-row head" role="row">
          <span role="columnheader" />
          <span role="columnheader">Free</span>
          <span role="columnheader" className="club">Garden Club</span>
        </div>
        {rows.map(([what, free, club]) => (
          <div key={what} className="gc-cmp-row" role="row">
            <span role="rowheader">{what}</span>
            <span role="cell">{free}</span>
            <span role="cell" className="club">{club}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function ClubFaq({ member }: { member: boolean }) {
  const items: { q: string; a: string }[] = [
    ...(member ? [] : [
      { q: "Do I lose anything if I don't join?", a: "No. The game is free to play, and you earn every achievement, badge and point the same way. Garden Club prizes you qualify for are held until you join." },
      { q: "How much is it?", a: `${SUB_COPY.price}, billed monthly through Stripe. There's no contract, and you can cancel whenever you like.` },
    ]),
    { q: "How do I cancel?", a: "From your Profile page, under Garden Club. It takes one click. You can also change your card or see your invoices there." },
    { q: "What happens when I cancel?", a: "Your membership carries on until the end of the month you've paid for, then stops renewing. You keep everything you collected, and any prizes you earn afterward are held in case you come back." },
    { q: "Does it work on every device?", a: "Yes. Membership belongs to your account, so it follows your garden to any computer you sign in on." },
  ];
  return (
    <section className="gc-section" aria-labelledby="gc-faq-h">
      <h2 id="gc-faq-h" className="gc-h2">Questions</h2>
      <div className="lp-faq gc-faq">
        {items.map((it) => <details key={it.q}><summary>{it.q}</summary><p>{it.a}</p></details>)}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
//  the smaller surfaces: home band, top button, nav pill, profile panel
// ---------------------------------------------------------------------------

/** THE HOME PAGE BAND — the invitation for a visitor, the member's own card for a member. */
export function ClubBand() {
  const { m, member, loading } = useMembership();
  if (loading) return null;
  if (member) {
    const s = m.subscription;
    return (
      <section className="gc-band member" aria-label="Your Garden Club membership">
        <ClubCrest size={64} glow />
        <div className="gc-band-copy">
          <h2>{SUB_COPY.memberHeadline}</h2>
          <p>
            {SUB_COPY.memberLine}
            {m.waiting > 0 ? ` ${SUB_COPY.heldCount(m.waiting)} in the game.` : ""}
            {s.cancelAtPeriodEnd && s.periodEnd ? ` Ends ${clubDate(s.periodEnd)}.` : ""}
          </p>
        </div>
        <Link to="/garden-club" className="gc-ghost">Your membership →</Link>
      </section>
    );
  }
  return (
    <section className="gc-band" aria-labelledby="gc-band-h">
      <PetalDrift n={8} />
      <ClubCrest size={92} glow />
      <div className="gc-band-copy">
        <span className="gc-kicker">{m.status === "lapsed" ? "Welcome back" : "Membership"}</span>
        <h2 id="gc-band-h">The Garden Club</h2>
        <ul className="gc-band-perks">{SUB_COPY.perks.map((p) => <li key={p}>{p}</li>)}</ul>
        {m.waiting > 0 && <p className="gc-waiting">🎁 <b>{SUB_COPY.heldCount(m.waiting)}</b></p>}
      </div>
      <div className="gc-band-cta">
        <Link to="/garden-club" className="gc-join">About Garden Club</Link>
        <span className="gc-join-note">{m.joinPaused ? "Opens with the full game" : `${SUB_COPY.price} · cancel any time`}</span>
      </div>
    </section>
  );
}

/** THE HOME PAGE TOP BUTTON — sits with What's New / Shop / Profile in the first screen. */
export function ClubTopButton() {
  const { m, member, loading } = useMembership();
  const sub = loading ? " " : member ? "You're a member" : m.joinPaused ? "Opens at the full release" : `Join · ${SUB_COPY.price}`;
  return (
    <Link to="/garden-club" className={`lp-topbtn club${member ? " member" : ""}`}>
      
      <span><b>Garden Club</b><small>{sub}</small></span>
    </Link>
  );
}

/** THE NAV PILL — on every site page but the club's own. "Join" for a visitor, a member's badge for a member;
 *  nothing for a visitor while joining is paused (Kickstarter demo), since there is nothing to join yet. */
export function ClubNavPill() {
  const { m, member, loading } = useMembership();
  const here = useLocation().pathname === "/garden-club";
  if (loading || here || (!member && m.joinPaused)) return null;
  return member
    ? <Link to="/garden-club" className="gc-pill member" title="Your Garden Club membership">Member</Link>
    : <Link to="/garden-club" className="gc-pill" title={`${SUB_COPY.name}: ${SUB_COPY.price}`}>Join Garden Club</Link>;
}

/**
 * THE PROFILE PANEL — where a member manages or cancels. Cancelling asks once, inline, and says plainly what
 * happens (perks stay until the paid month ends; collected prizes stay forever). Card and invoices go to
 * Stripe's own billing page, which comes back here.
 */
export function ClubProfilePanel() {
  const { m, member, loading, refresh } = useMembership();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState<"" | "cancel" | "resume" | "billing">("");
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const s = m.subscription;
  const end = clubDate(s.periodEnd);
  const hash = useLocation().hash;
  const panel = useRef<HTMLElement>(null);
  // "Manage membership" links here as /profile#garden-club; the router does not scroll to a hash on its own
  useEffect(() => {
    if (hash === "#garden-club" && !loading) panel.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, loading]);

  async function run(kind: "cancel" | "resume" | "billing") {
    setBusy(kind); setErr(""); setNote("");
    try {
      if (kind === "billing") { window.location.href = await openBillingPortal(); return; }
      const next = kind === "cancel" ? await cancelSubscription() : await resumeSubscription();
      await refresh();
      setConfirm(false);
      setNote(kind === "cancel"
        ? `Canceled. Your perks stay on until ${clubDate(next.subscription.periodEnd)}.`
        : "Welcome back. Your membership will renew as normal.");
    } catch (e) { setErr(String((e as Error).message || e)); }
    finally { setBusy(""); }
  }

  return (
    <section className="profile-section" id="garden-club" ref={panel}>
      <h2 className="profile-section-title">Garden Club {member && <span className="profile-title-note">member</span>}</h2>
      <div className={`profile-card gc-panel${member ? " member" : ""}`}>
        {loading ? <p className="profile-muted">Checking your membership…</p>
          : member ? (
            <>
              <div className="gc-panel-head">
                <ClubCrest size={56} glow />
                <div>
                  <b>{SUB_COPY.memberHeadline}</b>
                  <div className="profile-muted">
                    {!m.managed ? "Switched on by the dev toggle, with no billing attached."
                      : s.cancelAtPeriodEnd ? `Canceled · your perks stay on until ${end}`
                      : s.periodEnd ? `${SUB_COPY.price} · renews ${end}` : SUB_COPY.price}
                  </div>
                </div>
              </div>
              <ul className="gc-panel-perks">{SUB_COPY.perks.map((p) => <li key={p}>✓ {p}</li>)}</ul>
              {m.waiting > 0 && <p className="gc-panel-wait">🎁 {SUB_COPY.heldCount(m.waiting)}. Collect them in the game.</p>}
              {m.managed && !confirm && (
                <div className="gc-panel-actions">
                  <button className="site-btn ghost sm" disabled={!!busy} onClick={() => void run("billing")}>
                    {busy === "billing" ? "Opening…" : "Billing & invoices"}
                  </button>
                  {s.cancelAtPeriodEnd
                    ? <button className="site-btn primary sm" disabled={!!busy} onClick={() => void run("resume")}>{busy === "resume" ? "…" : "Keep my membership"}</button>
                    : <button className="site-btn ghost sm gc-cancel" disabled={!!busy} onClick={() => { setConfirm(true); setNote(""); }}>Cancel membership</button>}
                </div>
              )}
              {confirm && (
                <div className="gc-confirm" role="alertdialog" aria-label="Cancel Garden Club?">
                  <p><b>Cancel Garden Club?</b> You'll keep your perks until <b>{end}</b>, and you keep any prizes you've collected. Your membership won't renew after that.</p>
                  <div className="gc-panel-actions">
                    <button className="site-btn primary sm" disabled={!!busy} onClick={() => setConfirm(false)}>Keep my membership</button>
                    <button className="site-btn danger sm" disabled={!!busy} onClick={() => void run("cancel")}>{busy === "cancel" ? "Canceling…" : "Yes, cancel"}</button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="gc-panel-invite">
              <ClubCrest size={56} />
              <div>
                <b>{m.status === "lapsed" ? "Welcome back to the Garden Club" : "The Garden Club"}</b>
                <div className="profile-muted">
                  {m.status === "lapsed" ? SUB_COPY.lapsed : `${SUB_COPY.perks.join(" · ")} · ${SUB_COPY.price}.`}
                </div>
                {m.waiting > 0 && <p className="gc-panel-wait">🎁 {SUB_COPY.heldCount(m.waiting)}</p>}
              </div>
              <button className="site-btn primary sm" onClick={() => navigate("/garden-club")}>
                {m.status === "lapsed" ? "Rejoin" : "About Garden Club"}
              </button>
            </div>
          )}
        {note && <p className="gc-panel-note" role="status">✓ {note}</p>}
        {err && <p className="profile-err" role="alert">{err}</p>}
      </div>
    </section>
  );
}
